import React from 'react';
import { Popover, Grid, Avatar, Typography, Paper, Box, Chip } from '@mui/material';

interface Provider {
    RoleId: string;
    AssignedProviderId: string;
    ProviderName?: string;
    FirstName?: string;
    LastName?: string;
    FullName?: string;
    ProviderId?: string;
}

interface RoleNameById {
    [key: string]: string;
}

interface CareTeamPopoverProps {
    open: boolean;
    anchorEl: HTMLElement | null;
    onClose: () => void;
    teamRoles: Provider[];
    roleNameById: RoleNameById;
    allProviders: Provider[];
    isLoading?: boolean;
    avatarSize?: number;
    primaryRoleId?: string;
    memberId?: string;
    teamRoleAlternates?: any[];
}

const CareTeamPopover: React.FC<CareTeamPopoverProps> = ({
    open,
    anchorEl,
    onClose,
    teamRoles,
    roleNameById,
    allProviders,
    isLoading = false,
    avatarSize = 40,
    primaryRoleId,
    memberId,
    teamRoleAlternates = [],
}) => {
    // Determine if a provider is an alternate for this member
    const isProviderAlternate = (roleId: string, providerId: string): boolean => {
        return teamRoleAlternates.some((alt: any) => alt.MemberId === memberId && alt.RoleId === roleId && alt.AssignedProviderId === providerId);
    };

    // Determine if provider is both primary and alternate
    const isProviderBothPrimaryAndAlternate = (roleId: string, providerId: string): boolean => {
        const isPrimary = primaryRoleId === roleId;
        const isAlternate = isProviderAlternate(roleId, providerId);
        return isPrimary && isAlternate;
    };
    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
            transformOrigin={{ vertical: 'center', horizontal: 'left' }}
            PaperProps={{ sx: { width: 'auto', maxWidth: 500, p: 1, boxShadow: 1, borderRadius: 2, position: 'relative' } }}
            disableScrollLock={false}
        >
            <Box
                sx={{
                    position: 'absolute',
                    left: -18,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 0,
                    height: 0,
                    borderTop: '16px solid transparent',
                    borderBottom: '16px solid transparent',
                    borderRight: '18px solid',
                    borderRightColor: 'background.paper',
                    zIndex: 1,
                }}
            />
            {isLoading ? (
                <Typography>Loading...</Typography>
            ) : teamRoles.length > 0 ? (
                <Grid container spacing={1} sx={{ mt: 1, mb: 1, maxWidth: 480, margin: '0 auto', flexWrap: 'wrap' }}>
                    {teamRoles.map((tr: any) => {
                        // Check if this role has an alternate
                        const alternate = teamRoleAlternates?.find((alt: any) => alt.MemberId === memberId && alt.RoleId === tr.RoleId);
                        const provider = alternate || tr;

                        const roleName = roleNameById?.[provider.RoleId] || 'Provider';
                        let initials = '';
                        if (/^[A-Z]{2,4}$/.test(roleName.replace(/\s/g, ''))) {
                            initials = roleName.replace(/\s/g, '').slice(0, 4);
                        } else {
                            initials = roleName
                                .split(' ')
                                .map((word: string) => word[0])
                                .join('')
                                .toUpperCase();
                        }
                        const providerInfo = allProviders?.find(
                            (p: any) => p.Id === provider.AssignedProviderId || p.ProviderId === provider.AssignedProviderId
                        );
                        const providerName =
                            providerInfo?.ProviderName ||
                            providerInfo?.FullName ||
                            `${providerInfo?.FirstName || ''} ${providerInfo?.LastName || ''}`.trim() ||
                            provider.ProviderName ||
                            'Unassigned';

                        const isPrimary = primaryRoleId === tr.RoleId;
                        const isAlternate = isProviderAlternate(provider.RoleId, provider.AssignedProviderId);
                        const isBothPrimaryAndAlternate = isProviderBothPrimaryAndAlternate(provider.RoleId, provider.AssignedProviderId);

                        let borderColor = 'transparent';
                        let borderStyle = 'none';
                        let boxShadow = 1;
                        let avatarBgColor = 'primary.main';
                        let chipLabel = '';
                        let chipColor: 'success' | 'warning' | 'error' | 'info' | 'default' | 'primary' | 'secondary' = 'success';

                        if (isBothPrimaryAndAlternate) {
                            borderColor = '#9C27B0';
                            borderStyle = '2px solid';
                            boxShadow = 1;
                            avatarBgColor = '#9C27B0';
                            chipLabel = 'Primary Alternate';
                            chipColor = 'secondary';
                        } else if (isPrimary) {
                            borderColor = '#9C27B0';
                            borderStyle = '2px solid';
                            boxShadow = 1;
                            avatarBgColor = '#9C27B0';
                            chipLabel = 'Primary';
                            chipColor = 'secondary';
                        } else if (isAlternate) {
                            borderColor = 'success.main';
                            borderStyle = '2px solid';
                            boxShadow = 1;
                            avatarBgColor = 'success.main';
                            chipLabel = 'Alternate';
                            chipColor = 'success';
                        }

                        const displayName = providerName;

                        return (
                            <Grid
                                size={{ xs: 6, sm: 4, md: 3 }}
                                key={provider.RoleId + '_' + provider.AssignedProviderId}
                                sx={{ display: 'flex', justifyContent: 'center' }}
                            >
                                <Paper
                                    elevation={2}
                                    sx={{
                                        width: 120,
                                        height: 140,
                                        p: 0.5,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        borderRadius: 2,
                                        boxShadow: boxShadow,
                                        border: borderStyle,
                                        borderColor: borderColor,
                                        position: 'relative',
                                    }}
                                >
                                    {chipLabel && (
                                        <Chip
                                            label={chipLabel}
                                            size="small"
                                            color={chipColor}
                                            variant="filled"
                                            sx={{
                                                position: 'absolute',
                                                top: -8,
                                                fontSize: '10px',
                                                height: '20px',
                                                zIndex: 10,
                                            }}
                                        />
                                    )}
                                    <Avatar
                                        sx={{
                                            width: avatarSize,
                                            height: avatarSize,
                                            bgcolor: avatarBgColor,
                                            fontSize: avatarSize * 0.45,
                                            fontWeight: 500,
                                            mb: 0.5,
                                        }}
                                    >
                                        {initials}
                                    </Avatar>
                                    <Typography fontWeight={600} fontSize={15} align="center" sx={{ wordBreak: 'break-word', whiteSpace: 'normal', mb: 0.5 }}>
                                        {displayName}
                                    </Typography>
                                    <Typography color="text.secondary" fontSize={13} align="center" sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                                        {roleName}
                                    </Typography>
                                </Paper>
                            </Grid>
                        );
                    })}
                </Grid>
            ) : (
                <Typography align="center">No assigned providers</Typography>
            )}
        </Popover>
    );
};

export default CareTeamPopover;
